import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebounce } from 'use-debounce'
import {
  Button,
  ButtonBlock,
  FieldBlock,
  Font,
  Grid,
  List,
  Modal,
  ProgressBar,
  Section,
  TextBlock,
  TextField,
  useToast,
} from '@politechdev/blocks-design-system'
import { useRequest } from 'hooks'
import {
  AssociatedShift,
  PerformanceReview,
  SubmittablePerformanceReview,
  UpdateReviewRequestResult,
} from 'performanceReviews/types'
import {
  getIdsForShiftsUpdate,
  navigateToPacket,
  searchShifts,
} from 'performanceReviews/utils'
import ShiftListItem from './ShiftListItem'

const AddShiftsModal = ({
  isOpen,
  setIsOpen,
  performanceReview,
  updateReview,
}: {
  isOpen: boolean
  setIsOpen: (value: boolean) => void
  performanceReview: PerformanceReview
  updateReview: (
    fields: SubmittablePerformanceReview
  ) => UpdateReviewRequestResult
}) => {
  const { t } = useTranslation()
  const { setToast } = useToast()

  const [isUpdateLoading, setIsUpdateLoading] = useState(false)
  const [nameQuery, setNameQuery] = useState<string>('')
  const [debouncedQuery] = useDebounce(nameQuery, 250)

  const [selectedShifts, setSelectedShifts] = useState<AssociatedShift[]>([])

  const searchShiftsRequest = useRequest(searchShifts, {
    onError: () => {
      setToast({
        message: t('There was an issue gettings shifts'),
        variant: 'error',
      })
    },
  })

  useEffect(() => {
    if (!isOpen) {
      setNameQuery('')
      setSelectedShifts([])
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen) {
      void searchShiftsRequest.makeRequest(
        performanceReview.canvasser.id,
        performanceReview.id,
        debouncedQuery
      )
    }
  }, [isOpen, debouncedQuery])

  const toggleShift = (isSelected: boolean, shift: AssociatedShift) => {
    if (!isSelected) {
      setSelectedShifts(currentShifts => currentShifts.concat(shift))
    } else {
      setSelectedShifts(currentShifts =>
        currentShifts.filter(selected => selected.id !== shift.id)
      )
    }
  }

  const handleUpdate = async () => {
    const { flagIds, shiftIds } = getIdsForShiftsUpdate(
      performanceReview,
      selectedShifts
    )

    setIsUpdateLoading(true)
    const response = await updateReview({
      flag_ids: flagIds,
      shift_ids: shiftIds,
    })
    setIsUpdateLoading(false)

    if (response?.performance_review) {
      setIsOpen(false)
    }
  }

  const onContinue = () => {
    void handleUpdate()
  }

  const onCancel = () => {
    setIsOpen(false)
  }

  return (
    <Modal title={t('Add packet')} isOpen={isOpen}>
      <Modal.Body>
        <FieldBlock>
          <TextField
            label={t('Packet name')}
            type="search"
            value={nameQuery}
            onChange={value => setNameQuery(value as string)}
          />
        </FieldBlock>
        <Section>
          <ProgressBar show={isUpdateLoading} />
          <List
            loading={searchShiftsRequest.isLoading}
            emptyMessage={t('No matching packets found')}
            itemData={searchShiftsRequest.response?.shifts ?? []}
            render={item => {
              const shift = item as AssociatedShift
              const isSelected = selectedShifts.some(
                selected => selected.id === shift.id
              )
              return (
                <ShiftListItem shift={shift}>
                  <Button.Secondary onClick={() => navigateToPacket(shift)}>
                    {t('View')}
                  </Button.Secondary>
                  {isSelected ? (
                    <Button.Secondary
                      onClick={() => toggleShift(isSelected, shift)}
                    >
                      {t('Deselect')}
                    </Button.Secondary>
                  ) : (
                    <Button onClick={() => toggleShift(isSelected, shift)}>
                      {t('Select')}
                    </Button>
                  )}
                </ShiftListItem>
              )
            }}
          />
        </Section>
      </Modal.Body>
      <Modal.Actions>
        <Grid>
          <TextBlock>
            <Font.Copy variant="hint">
              {t('{{ count }} packets selected', {
                count: selectedShifts.length,
              })}
            </Font.Copy>
          </TextBlock>
          <ButtonBlock justify="right">
            <Button.Secondary onClick={onCancel}>
              {t('Cancel')}
            </Button.Secondary>
            <Button.Accent onClick={onContinue}>{t('Submit')}</Button.Accent>
          </ButtonBlock>
        </Grid>
      </Modal.Actions>
    </Modal>
  )
}

export default AddShiftsModal
